import React, { useEffect, useState } from "react";
import "./EducationLevelSection.css";
import Layout from "../Components/Website/Layout";
import { Row, Col, Card } from "react-bootstrap";
import axios from "axios";
import { ACADIMAC, baseURL } from "../API/API";
import useAuth from "../Pages/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Import generated realistic images
import Img1 from "../Imge/7.png";
import Img2 from "../Imge/8.png";
import Img3 from "../Imge/3.png";
import Img4 from "../Imge/4.png";
import Img5 from "../Imge/5.png";
import Img6 from "../Imge/6.png";
import FallbackImg from "../Imge/about_2.png";

const fallbackStages = [
  { id: 1, name: "الصف الأول الثانوي", academic_type: "general" },
  { id: 2, name: "الصف الثاني الثانوي", academic_type: "general" },
  { id: 3, name: "الصف الثالث الثانوي", academic_type: "general" },
  { id: 4, name: "الصف الأول الثانوي أزهري", academic_type: "azhar" },
  { id: 5, name: "الصف الثاني الثانوي أزهري", academic_type: "azhar" },
  { id: 6, name: "الصف الثالث الثانوي أزهري", academic_type: "azhar" },
];

// ✅ Helper: detect azhar
const isAzharStage = (stage) => {
  const name = stage.name?.toLowerCase() || "";
  const type = stage.academic_type?.toLowerCase() || "";
  return (
    type === "azhar" ||
    name.includes("أزهر") ||
    name.includes("ازهر")
  );
};

// ✅ Helper: detect grade order (1=أول, 2=ثاني, 3=ثالث)
const getGradeOrder = (name) => {
  const n = name?.toLowerCase() || "";
  if (n.includes("أول") || n.includes("اول") || n.includes("1")) return 1;
  if (n.includes("ثان") || n.includes("تان") || n.includes("2")) return 2;
  if (n.includes("ثالث") || n.includes("تالت") || n.includes("3")) return 3;
  return 4;
};

// ✅ Sort: مرتبة حسب ترتيب العرض المدخل في الداشبورد أولاً، ثم حسب القسم والصف كاحتياطي
const sortStages = (stages) => {
  return [...stages].sort((a, b) => {
    const orderA = a.sort_order ?? a.sortOrder ?? a.order ?? 0;
    const orderB = b.sort_order ?? b.sortOrder ?? b.order ?? 0;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    const aIsAzhar = isAzharStage(a) ? 1 : 0;
    const bIsAzhar = isAzharStage(b) ? 1 : 0;
    if (aIsAzhar !== bIsAzhar) return aIsAzhar - bIsAzhar;
    return getGradeOrder(a.name) - getGradeOrder(b.name);
  });
};

export default function EducationLevelSection() {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { auth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${baseURL}/${ACADIMAC}`)
      .then((response) => {
        const data = response.data?.data || [];
        if (data.length > 0) {
          setStages(sortStages(data));
        } else {
          setStages(fallbackStages);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching academic years for landing page:", error);
        setStages(fallbackStages);
        setLoading(false);
      });
  }, []);

  const handleCardClick = (stage) => {
    if (!auth?.accessToken) {
      toast.info("يرجى إنشاء حساب أو تسجيل الدخول لعرض محتوى الكورس");
      navigate("/register", { state: { selectedStageId: stage.id } });
      return;
    }
    if (auth.user?.is_staff) {
      navigate(`/learn/${stage.id}`);
      return;
    }
    const enrolledId = auth.user?.academic_year_id;
    if (Number(stage.id) !== Number(enrolledId)) {
      toast.error(
        "أنت لست مسجلاً في هذه المرحلة. يمكنك فقط الدخول إلى مرحلتك المسجلة."
      );
      return;
    }
    navigate(`/learn/${stage.id}`);
  };

  const getStageImage = (stage) => {
    if (stage?.image_path) return stage.image_path;
    return FallbackImg;
  };

  const getStageDetails = (stage) => {
    const title = stage?.name || stage?.academicName || "";
    const azhar = isAzharStage(stage);
    const subtitle = stage?.subtitle || (azhar ? "ثانوية أزهرية" : "ثانوي عام");
    const desc = stage?.description || "";

    return {
      title: title,
      subtitle: subtitle,
      desc: desc,
    };
  };

  return (
    <div id="courses-section" className="education-level-section">
      <Layout dir="rtl">
        {/* Hanging "عايز سنة كام ؟" Title */}
        <div className="d-flex justify-content-center w-100 mb-5">
          <div className="hanging-title-container text-center">
            <div className="hanging-chains">
              <span className="chain-link-left"></span>
              <span className="chain-link-right"></span>
            </div>
            <h2 className="hanging-title m-0">عايز سنة كام ؟</h2>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
            <p className="mt-3 text-muted">جاري تحميل المراحل الدراسية...</p>
          </div>
        ) : stages.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p>لا توجد مراحل دراسية متاحة حالياً.</p>
          </div>
        ) : (
          <Row
            className="justify-content-center g-4 mt-4"
            style={{ direction: "rtl" }}
          >
            {stages.map((stage) => {
              const img = getStageImage(stage);
              const details = getStageDetails(stage);
              const isMyCourse =
                auth?.accessToken &&
                (auth.user?.is_staff ||
                  Number(stage.id) === Number(auth.user?.academic_year_id));

              return (
                <Col key={stage.id} xs={12} sm={6} md={4} className="d-flex">
                  <Card
                    className={`stage-card w-100 ${isMyCourse ? "my-stage" : ""}`}
                  >
                    <div className="stage-image-wrapper">
                      <Card.Img
                        variant="top"
                        src={img}
                        className="stage-card-img"
                      />
                    </div>
                    <Card.Body
                      className="d-flex flex-column text-end"
                      style={{ padding: "24px 20px" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <Card.Title className="stage-card-title-custom m-0">
                          {details.title}
                        </Card.Title>
                        <span className="stage-card-subtitle-custom">
                          {details.subtitle}
                        </span>
                      </div>
                      <Card.Text className="stage-card-desc-custom flex-grow-1 mb-4">
                        {details.desc}
                      </Card.Text>
                      <div className="d-flex justify-content-start">
                        <span
                          onClick={() => handleCardClick(stage)}
                          className="stage-card-link-custom"
                        >
                          خش شوف كورساتك
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Layout>
    </div>
  );
}